import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { Star, Trash2, Plus, BarChart2, Clock } from 'lucide-react';
import { AppLayout, PageBreadcrumb } from '@/components/AppLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/StatusBadge';
import { LivePriceBadge } from '@/components/LivePriceBadge';
import { useWatchlist, useRemoveFromWatchlist } from '@/hooks/useWatchlist';
import type { WatchlistItem } from '@/types/stock';

function WatchlistRow({ item, onRemove }: { item: WatchlistItem; onRemove: () => void }) {
  const daysSinceEval = item.lastEvaluationDate
    ? Math.floor((Date.now() / 1000 - item.lastEvaluationDate) / 86400)
    : null;

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/20 transition-colors">
      {/* Ticker + name */}
      <div className="min-w-[140px]">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono font-bold text-base">{item.ticker}</span>
          {item.lastEvaluationStatus && (
            <StatusBadge status={item.lastEvaluationStatus} size="sm" />
          )}
        </div>
        <p className="text-xs text-muted-foreground truncate max-w-[130px]">{item.companyName}</p>
      </div>

      {/* Live price */}
      <div className="flex-1">
        <LivePriceBadge ticker={item.ticker} compact />
      </div>

      {/* Last eval date */}
      <div className="text-right text-xs text-muted-foreground shrink-0 hidden sm:block">
        {daysSinceEval !== null ? (
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>{daysSinceEval === 0 ? 'Today' : `${daysSinceEval}d ago`}</span>
          </div>
        ) : (
          <span className="italic">Not analyzed</span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 shrink-0">
        {item.lastEvaluationId ? (
          <Button variant="outline" size="sm" asChild>
            <Link to={`/evaluation/${item.lastEvaluationId}`}>
              <BarChart2 className="w-3.5 h-3.5 mr-1" /> View
            </Link>
          </Button>
        ) : (
          <Button variant="outline" size="sm" asChild>
            <Link to={`/analyze?ticker=${item.ticker}`}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Analyze
            </Link>
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          className="text-destructive hover:text-destructive h-8 w-8 p-0"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

export default function WatchlistPage() {
  useSeoMeta({ title: 'Watchlist — Stock Fundamentals', description: 'Your stock watchlist with live prices.' });

  const navigate = useNavigate();
  const { data: watchlist, isLoading } = useWatchlist();
  const { mutate: removeItem } = useRemoveFromWatchlist();

  const sorted = (watchlist ?? []).sort((a, b) => b.addedAt - a.addedAt);

  const passCount = sorted.filter((i) => i.lastEvaluationStatus === 'PASS').length;
  const watchCount = sorted.filter((i) => i.lastEvaluationStatus === 'WATCH').length;
  const failCount = sorted.filter((i) => i.lastEvaluationStatus === 'FAIL').length;
  const unanalyzedCount = sorted.filter((i) => !i.lastEvaluationStatus).length;

  return (
    <AppLayout>
      <div className="max-w-screen-lg mx-auto px-4 py-8">
        <PageBreadcrumb items={[{ label: 'Dashboard', href: '/' }, { label: 'Watchlist' }]} />

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Star className="w-7 h-7 text-amber-500 fill-amber-500" />
              Watchlist
            </h1>
            <p className="text-muted-foreground mt-1">
              {sorted.length} stocks tracked · live prices via Yahoo Finance (no key needed)
            </p>
          </div>
          <Button onClick={() => navigate('/analyze')} className="gap-2">
            <Plus className="w-4 h-4" /> Add Stock
          </Button>
        </div>

        {/* Stats */}
        {sorted.length > 0 && (
          <div className="grid grid-cols-4 gap-3 mb-6">
            {[
              { label: 'PASS', value: passCount, color: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20' },
              { label: 'WATCH', value: watchCount, color: 'text-amber-600 bg-amber-50 dark:bg-amber-900/20' },
              { label: 'FAIL', value: failCount, color: 'text-red-600 bg-red-50 dark:bg-red-900/20' },
              { label: 'Unanalyzed', value: unanalyzedCount, color: 'text-muted-foreground bg-muted/40' },
            ].map((stat) => (
              <Card key={stat.label} className={stat.color}>
                <CardContent className="py-3 text-center">
                  <p className="text-xl font-bold">{stat.value}</p>
                  <p className="text-xs font-medium">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* List */}
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 p-3 border rounded-lg">
                <Skeleton className="h-12 w-48" />
                <div className="flex-1" />
                <Skeleton className="h-8 w-20" />
              </div>
            ))}
          </div>
        ) : sorted.length > 0 ? (
          <div className="space-y-2">
            {sorted.map((item) => (
              <WatchlistRow
                key={item.ticker}
                item={item}
                onRemove={() => removeItem(item.ticker)}
              />
            ))}
          </div>
        ) : (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center">
              <Star className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <CardTitle className="mb-2">Your watchlist is empty</CardTitle>
              <CardDescription className="mb-6">
                Analyze a stock to automatically add it to your watchlist.
              </CardDescription>
              <Button onClick={() => navigate('/analyze')} className="gap-2">
                <Plus className="w-4 h-4" /> Analyze a Stock
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
